/**
 * PhysicianSelector Component
 *
 * Multi-select component for choosing physicians using unified search
 * Searches RepConnect database + NPI Registry (exactly like Find Customers)
 * Shows conflict warnings for selected physicians
 */

import React, { useState, useEffect, useCallback } from 'react';
import { availabilityService } from '../services/availabilityService';
import { UnifiedProviderSearch } from '../utils/unifiedProviderSearch';
import { logger } from '../utils/logger';
import { Users, AlertCircle, CheckCircle2, UserPlus, Search, MapPin, User, X } from 'lucide-react';

const PhysicianSelector = ({
  locationId,
  selectedDate,
  selectedPhysicians,
  onPhysiciansChange,
  onPhysicianDataChange, // Optional: callback to pass full physician objects
  organizerId
}) => {
  // Search state (same as FindProvidersView)
  const [searchName, setSearchName] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [searchSpecialty, setSearchSpecialty] = useState('all');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searchMetadata, setSearchMetadata] = useState({
    registeredCount: 0,
    npiCount: 0,
    total: 0,
    hasMore: false
  });

  // Conflict checking state
  const [conflicts, setConflicts] = useState({});
  const [checkingConflicts, setCheckingConflicts] = useState(false);

  // Unified provider search (same as FindProvidersView)
  const searchUnifiedProviders = useCallback(async () => {
    if (!searchName.trim() && !searchLocation.trim() && searchSpecialty === 'all') {
      setSearchResults([]);
      setSearchError('');
      setSearchMetadata({ registeredCount: 0, npiCount: 0, total: 0, hasMore: false });
      return;
    }

    setIsSearching(true);
    setSearchError('');

    try {
      const searchParams = {
        name: searchName,
        location: searchLocation,
        specialty: searchSpecialty,
        limit: 20,
        offset: 0
      };

      logger.debug('PhysicianSelector: Searching providers', searchParams);
      const results = await UnifiedProviderSearch.searchProviders(searchParams);

      // Filter out the organizer from results
      const filteredResults = results.providers.filter(provider => {
        // Check both id and userId fields
        return provider.id !== organizerId && provider.userId !== organizerId;
      });

      setSearchResults(filteredResults);
      setSearchMetadata({
        registeredCount: results.registeredCount,
        npiCount: results.npiCount,
        total: filteredResults.length,
        hasMore: results.hasMore
      });

      logger.debug(`PhysicianSelector: Found ${filteredResults.length} providers (excluded organizer)`);

    } catch (error) {
      logger.error('PhysicianSelector search error', { error: error.message });
      setSearchError(`Search failed: ${error.message}`);
      setSearchResults([]);
      setSearchMetadata({ registeredCount: 0, npiCount: 0, total: 0, hasMore: false });
    } finally {
      setIsSearching(false);
    }
  }, [searchName, searchLocation, searchSpecialty, organizerId]);

  // Debounced search effect (500ms delay)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchUnifiedProviders();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchName, searchLocation, searchSpecialty, searchUnifiedProviders]);

  // Check for conflicts when physicians are selected
  useEffect(() => {
    const checkConflicts = async () => {
      if (!selectedDate || selectedPhysicians.length === 0) {
        setConflicts({});
        return;
      }

      try {
        setCheckingConflicts(true);
        const conflictMap = {};

        // Check each physician for conflicts
        for (const physicianId of selectedPhysicians) {
          // Check for conflicts at common meeting times
          const times = ['11:00', '11:30', '12:00', '12:30', '13:00'];
          let hasAnyConflict = false;

          for (const time of times) {
            const hasConflict = await availabilityService.hasTimeConflict(
              physicianId,
              selectedDate,
              time
            );
            if (hasConflict) {
              hasAnyConflict = true;
              break;
            }
          }

          conflictMap[physicianId] = hasAnyConflict;
        }

        setConflicts(conflictMap);
      } catch (error) {
        logger.error('Error checking conflicts:', error);
      } finally {
        setCheckingConflicts(false);
      }
    };

    checkConflicts();
  }, [selectedPhysicians, selectedDate]);

  const handleTogglePhysician = (provider) => {
    // Use provider's id (which could be userId for registered providers)
    const physicianId = provider.userId || provider.id;

    if (selectedPhysicians.includes(physicianId)) {
      // Remove physician
      onPhysiciansChange(selectedPhysicians.filter(id => id !== physicianId));
    } else {
      // Add physician
      onPhysiciansChange([...selectedPhysicians, physicianId]);
    }

    // Also pass the full physician data if callback is provided
    if (onPhysicianDataChange) {
      onPhysicianDataChange(physicianId, provider, !selectedPhysicians.includes(physicianId));
    }
  };

  const isPhysicianSelected = (provider) => {
    const physicianId = provider.userId || provider.id;
    return selectedPhysicians.includes(physicianId);
  };

  return (
    <div>
      {/* Header with count */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium text-gray-700">
          Search for additional physicians to invite
        </p>
        {selectedPhysicians.length > 0 && (
          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
            {selectedPhysicians.length} selected
          </span>
        )}
      </div>

      {/* Search Fields - Same as FindProvidersView */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
        <div className="flex flex-col gap-3">
          {/* Name Search */}
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Doctor name (e.g., Smith, Dr. Jones)"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          {/* Location Search */}
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Location (city, state, or ZIP)"
              value={searchLocation}
              onChange={(e) => setSearchLocation(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          {/* Specialty Filter */}
          <select
            value={searchSpecialty}
            onChange={(e) => setSearchSpecialty(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value="all">All Specialties</option>
            <option value="anesthesiology">Anesthesiology</option>
            <option value="cardiology">Cardiology</option>
            <option value="dermatology">Dermatology</option>
            <option value="emergency">Emergency Medicine</option>
            <option value="endocrinology">Endocrinology</option>
            <option value="family">Family Medicine</option>
            <option value="gastroenterology">Gastroenterology</option>
            <option value="infectious">Infectious Disease</option>
            <option value="internal">Internal Medicine</option>
            <option value="nephrology">Nephrology</option>
            <option value="neurology">Neurology</option>
            <option value="obstetrics">Obstetrics & Gynecology</option>
            <option value="oncology">Oncology</option>
            <option value="ophthalmology">Ophthalmology</option>
            <option value="orthopedics">Orthopedics</option>
            <option value="otolaryngology">Otolaryngology (ENT)</option>
            <option value="pathology">Pathology</option>
            <option value="pediatrics">Pediatrics</option>
            <option value="plastic">Plastic Surgery</option>
            <option value="psychiatry">Psychiatry</option>
            <option value="pulmonology">Pulmonology</option>
            <option value="radiology">Radiology</option>
            <option value="rheumatology">Rheumatology</option>
            <option value="surgery">Surgery</option>
            <option value="urology">Urology</option>
          </select>

          {/* Clear Search Button */}
          {(searchName || searchLocation || searchSpecialty !== 'all') && (
            <button
              onClick={() => {
                setSearchName('');
                setSearchLocation('');
                setSearchSpecialty('all');
              }}
              className="flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
              Clear Search
            </button>
          )}
        </div>

        {/* Search Info Banner */}
        {(searchName || searchLocation || searchSpecialty !== 'all') && (
          <div className="mt-3 p-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg flex items-center space-x-2">
            <Search className="w-4 h-4 flex-shrink-0" />
            <span className="text-xs">
              Searching RepConnect database and NPI Registry
              {searchMetadata.total > 0 && (
                <span className="ml-1">
                  • Found {searchMetadata.registeredCount} registered and {searchMetadata.npiCount} directory physicians
                </span>
              )}
            </span>
          </div>
        )}

        {/* Error Message */}
        {searchError && (
          <div className="mt-3 p-2 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center space-x-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span className="text-xs">{searchError}</span>
          </div>
        )}
      </div>

      {/* Loading State */}
      {isSearching && (
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-sm text-gray-600">Searching physicians...</p>
          </div>
        </div>
      )}

      {/* Conflict Checking Banner */}
      {checkingConflicts && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            Checking physician availability...
          </p>
        </div>
      )}

      {/* Search Results */}
      {!isSearching && searchResults.length > 0 && (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {searchResults.map((provider) => {
            const physicianId = provider.userId || provider.id;
            const isSelected = isPhysicianSelected(provider);
            const hasConflict = conflicts[physicianId];

            return (
              <div
                key={provider.id}
                onClick={() => handleTogglePhysician(provider)}
                className={`
                  relative p-3 border rounded-lg cursor-pointer transition-all
                  ${isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                  }
                  ${hasConflict ? 'ring-1 ring-yellow-400' : ''}
                `}
              >
                <div className="flex items-center">
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleTogglePhysician(provider)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    onClick={(e) => e.stopPropagation()}
                  />

                  {/* Physician details */}
                  <div className="ml-3 flex-1">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h5 className="text-sm font-medium text-gray-900">
                          {provider.name}
                        </h5>
                        <div className="flex items-center gap-2 mt-1">
                          {provider.specialty && (
                            <p className="text-xs text-gray-600">
                              {provider.specialty}
                            </p>
                          )}
                          {provider.hospital && (
                            <p className="text-xs text-gray-500">
                              • {provider.hospital}
                            </p>
                          )}
                        </div>
                        {provider.address && (
                          <p className="text-xs text-gray-500 mt-1">
                            {provider.address}
                          </p>
                        )}
                      </div>

                      {/* Status badges */}
                      <div className="flex items-center gap-1 ml-2">
                        {provider.isRegistered ? (
                          <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded whitespace-nowrap">
                            RepConnect
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded whitespace-nowrap">
                            NPI
                          </span>
                        )}
                        {hasConflict && isSelected && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-700 rounded flex items-center gap-1 whitespace-nowrap">
                            <AlertCircle className="w-3 h-3" />
                            Conflict
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {!isSearching && searchResults.length === 0 && (searchName || searchLocation || searchSpecialty !== 'all') && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600">No physicians found</p>
          <p className="text-xs text-gray-500 mt-1">Try different search terms or location</p>
        </div>
      )}

      {/* Initial State - No Search */}
      {!isSearching && searchResults.length === 0 && !searchName && !searchLocation && searchSpecialty === 'all' && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <Search className="w-12 h-12 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600">Search to add physicians</p>
          <p className="text-xs text-gray-500 mt-1">Enter a name, location, or select a specialty to search</p>
        </div>
      )}

      {/* Conflict Warning */}
      {selectedPhysicians.length > 0 && Object.values(conflicts).some(hasConflict => hasConflict) && (
        <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-xs text-yellow-800">
            <strong>Note:</strong> Physicians with conflicts will receive RSVP requests to confirm availability.
          </p>
        </div>
      )}
    </div>
  );
};

export default PhysicianSelector;
